import React, { useEffect } from 'react';
import CampusHeader from '../../components/feature/CampusHeader';
import Footer from '../../components/feature/Footer';

export default function AcceptableUsePage() {
  useEffect(() => {
    document.title = "Acceptable Use Policy | Campus Market";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <CampusHeader />

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 via-teal-500 to-indigo-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-white/20">Policy</span>
            <span className="text-white/80 text-sm">Updated periodically</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Acceptable Use Policy</h1>
          <p className="mt-3 max-w-3xl text-white/90">
            Use Campus Market responsibly and lawfully. These rules keep buying, selling,
            and messaging safe across Nigerian campuses.
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Quick nav */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-1">
              <div className="bg-white border rounded-2xl shadow-sm p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">On this page</h2>
                <nav className="space-y-2 text-sm">
                  <a href="#principles" className="block text-gray-700 hover:text-teal-600">General Principles</a>
                  <a href="#prohibited" className="block text-gray-700 hover:text-teal-600">Prohibited Activities</a>
                  <a href="#listings" className="block text-gray-700 hover:text-teal-600">Listing Standards</a>
                  <a href="#comms" className="block text-gray-700 hover:text-teal-600">Messaging & Calls</a>
                  <a href="#payments" className="block text-gray-700 hover:text-teal-600">Payments</a>
                  <a href="#enforcement" className="block text-gray-700 hover:text-teal-600">Enforcement</a>
                  <a href="#updates" className="block text-gray-700 hover:text-teal-600">Updates</a>
                </nav>
              </div>
            </div>

            {/* Sections */}
            <div className="lg:col-span-2 space-y-8">
              <section id="principles" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-shield-check-line text-teal-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">General Principles</h2>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Use Campus Market lawfully, responsibly, and with respect for other users.</li>
                  <li>Only list items you are legally entitled to sell; represent them accurately.</li>
                  <li>Do not interfere with platform security or other users’ accounts.</li>
                </ul>
              </section>

              <section id="prohibited" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-close-circle-line text-red-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Prohibited Activities</h2>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Illegal, fraudulent, deceptive, or misleading activities.</li>
                  <li>Harassment, hate speech, threats, or abusive content.</li>
                  <li>Sale/promotion of prohibited items (weapons, drugs, stolen or counterfeit goods).</li>
                  <li>Payment manipulation, chargeback abuse, or money laundering.</li>
                  <li>Spamming, scraping, automated data collection, or excessive API usage.</li>
                  <li>IP infringement (unauthorized trademarks/copyrights).</li>
                  <li>Bypassing/probing security or interfering with listings/accounts.</li>
                </ul>
              </section>

              <section id="listings" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-price-tag-3-line text-indigo-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Listing Standards</h2>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Provide accurate titles, descriptions, prices, and photos.</li>
                  <li>Do not misrepresent condition, authenticity, or availability.</li>
                  <li>Follow your campus policies for on‑campus exchanges.</li>
                </ul>
              </section>

              <section id="comms" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-message-3-line text-teal-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Messaging & Calls</h2>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Communicate respectfully; do not send threats, explicit content, or spam.</li>
                  <li>Use calls/voice notes responsibly; do not record or share without consent.</li>
                </ul>
              </section>

              <section id="payments" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-bank-card-line text-purple-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Payments</h2>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Use supported payment methods only; do not force off‑platform payments.</li>
                  <li>Comply with Paystack and other payment partners’ AML/CFT rules.</li>
                </ul>
              </section>

              <section id="enforcement" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-scales-2-line text-amber-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Enforcement</h2>
                </div>
                <p className="text-gray-700">
                  Violations may result in listing removal, account suspension, or termination. We may cooperate with law enforcement
                  where required. Report violations to <a href="mailto:hello@synccore.com" className="text-teal-600 underline">hello@synccore.com</a>.
                </p>
              </section>

              <section id="updates" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-refresh-line text-teal-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Updates</h2>
                </div>
                <p className="text-gray-700">We may update this AUP periodically. Continued use constitutes acceptance of any changes.</p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}